import type {
  AccountingInvoicePostingMode,
  AccountingPaymentSyncMode,
  AccountingPostingCatalogAccount,
  AccountingPostingCatalogTaxRate,
  AccountingProvider,
} from '../../../../../services/accountingIntegration';
import { cn } from '@/lib/utils';

export const ACCOUNTING_FEATURE_DESCRIPTION =
  'Connect Xero or QuickBooks Online to automatically sync lesson payments, refunds, payouts, and customer records to your accounting software.';

export const PROVIDER_LABELS: Record<AccountingProvider, string> = {
  xero: 'Xero',
  quickbooks: 'QuickBooks Online',
};

export const INVOICE_POSTING_MODE_OPTIONS: {
  value: AccountingInvoicePostingMode;
  label: string;
}[] = [
  { value: 'authorised', label: 'Authorised — post paid invoices automatically' },
  { value: 'submitted', label: 'Submitted — send invoices for approval/review' },
  { value: 'draft', label: 'Draft — create invoices for accountant review' },
];

export const PAYMENT_SYNC_MODE_OPTIONS: {
  value: AccountingPaymentSyncMode;
  label: string;
}[] = [
  { value: 'immediate', label: 'Immediate — create payment with authorised invoice' },
  { value: 'invoice_only', label: 'Invoice only — do not sync payments automatically' },
];

export function accountingCatalogRefreshStorageKey(academyId: string) {
  return `courtsync.accountingCatalogRefreshAt.v2.${academyId}`;
}

export const selectClassName = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm',
  'focus-visible:outline-none focus-visible:border-primary-500 focus-visible:ring-1 focus-visible:ring-primary-500',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

export function accountOptionLabel(account: AccountingPostingCatalogAccount) {
  const base = account.name || account.id;
  return account.classification ? `${base} — ${account.classification}` : base;
}

export function taxOptionLabel(taxRate: AccountingPostingCatalogTaxRate) {
  const base = taxRate.name || taxRate.id;
  if (taxRate.totalTaxRate == null) return base;
  const rate = taxRate.totalTaxRate;
  const pct = rate <= 1 ? rate * 100 : rate;
  return `${base} (${pct.toFixed(2)}%)`;
}

export function buildAccountSelectOptions(
  catalog: AccountingPostingCatalogAccount[] | null,
  current: string
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [
    { value: '', label: 'Automatic — let CourtSync decide' },
  ];
  const inList = current && catalog?.some((account) => account.id === current);
  if (current && !inList) {
    options.push({
      value: current,
      label: 'Saved selection (not in current list — save to clear)',
    });
  }
  catalog?.forEach((account) => {
    options.push({ value: account.id, label: accountOptionLabel(account) });
  });
  return options;
}

export function buildTaxSelectOptions(
  catalog: AccountingPostingCatalogTaxRate[] | null,
  current: string
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [
    { value: '', label: 'Automatic — let CourtSync decide' },
  ];
  const inList = current && catalog?.some((taxRate) => taxRate.id === current);
  if (current && !inList) {
    options.push({
      value: current,
      label: 'Saved selection (not in current list — save to clear)',
    });
  }
  catalog?.forEach((taxRate) => {
    options.push({ value: taxRate.id, label: taxOptionLabel(taxRate) });
  });
  return options;
}
